import API_BASE from '../../utils/api';
import { formatLocalDate } from '../../utils/date';
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// Add this import at the top
import ProfileDropdown from "../../components/ProfileDropdown";

// Replace the nav section with this:

const ParentAttendance = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState([]);
  const [childName, setChildName] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [stats, setStats] = useState({
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    onLeave: 0,
    total: 0,
  });

  useEffect(() => {
    fetchAttendance();
  }, [user, selectedMonth]);

  const fetchAttendance = async () => {
    try {
      // Whole-day (period 0) records only, scoped to the selected month
      // server-side so this stays correct/fast regardless of how much
      // history exists (the endpoints' default 90-record cap otherwise
      // silently truncated older months' data client-side).
      const monthStart = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
      const monthEnd = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
      const params = `startDate=${formatLocalDate(monthStart)}&endDate=${formatLocalDate(monthEnd)}&period=0`;

      let allAttendance = [];

      if (user?.studentId) {
        // Use student ID if available from login
        const response = await axios.get(
          `${API_BASE}/attendance/student/${user.studentId}?${params}`,
        );
        allAttendance = response.data;
      } else {
        // Fallback: use my-child endpoint that auto-resolves from parent ID
        const response = await axios.get(
          `${API_BASE}/attendance/my-child?${params}`,
        );
        allAttendance = response.data.attendance || [];
        if (response.data.student) {
          setChildName(response.data.student.name);
        }
      }

      setAttendance(allAttendance);

      // Calculate stats
      const present = allAttendance.filter((r) => r.status === "present").length;
      const absent = allAttendance.filter((r) => r.status === "absent").length;
      const late = allAttendance.filter((r) => r.status === "late").length;
      const halfDay = allAttendance.filter((r) => r.status === "half-day").length;
      const onLeave = allAttendance.filter((r) => r.status === "on-leave").length;

      setStats({
        present,
        absent,
        late,
        halfDay,
        onLeave,
        total: allAttendance.length,
      });
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "present":
        return "bg-green-100 text-green-800 border-green-200";
      case "absent":
        return "bg-red-100 text-red-800 border-red-200";
      case "late":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "half-day":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "on-leave":
        return "bg-purple-100 text-purple-800 border-purple-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 bg-gray-100 flex items-center justify-center h-screen">
          <div className="text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 bg-gray-100 min-h-screen">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">
              Attendance History
            </h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          {/* Month Selector */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">
                Select Month:
              </label>
              <DatePicker
                selected={selectedMonth}
                onChange={(date) => setSelectedMonth(date)}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                className="border rounded-md px-3 py-2"
              />
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Total Days</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Present</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.present}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Absent</p>
              <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Late</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.late}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Half Day</p>
              <p className="text-2xl font-bold text-blue-600">{stats.halfDay}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">On Leave</p>
              <p className="text-2xl font-bold text-purple-600">{stats.onLeave}</p>
            </div>
          </div>

          {/* Attendance Percentage */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">
              Attendance Percentage
            </h2>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-green-500 h-4 rounded-full"
                    style={{
                      width: `${stats.total > 0 ? (stats.present / stats.total) * 100 : 0}%`,
                    }}
                  ></div>
                </div>
              </div>
              <span className="font-bold text-lg">
                {stats.total > 0
                  ? Math.round((stats.present / stats.total) * 100)
                  : 0}
                %
              </span>
            </div>
          </div>

          {/* Attendance Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Daily Attendance</h2>
            </div>
            <div className="p-6">
              {attendance.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Day
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Class
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {attendance.map((record) => (
                      <tr key={record._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {new Date(record.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {new Date(record.date).toLocaleDateString("en-US", {
                            weekday: "long",
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(record.status)}`}
                          >
                            {record.status.charAt(0).toUpperCase() +
                              record.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {record.classId?.className} -{" "}
                          {record.classId?.section}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    No attendance records found for this month
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentAttendance;
